<template>
  <div>
    <div class="modal-header" style="will-change: transform">
      <div class="row">
        <div class="col-12">
          <h3>
            <span id="bookingHeading"
              >Booking&nbsp;
              <span class="text-navy" id="bookingHours">
                <i class="fa fa-clock-o"></i>
                {{ hours === 1 ? '1 hour' : hours + ' hours' }}
              </span>
            </span>
          </h3>
        </div>
      </div>
    </div>

    <form name="bookingForm" autocomplete="off" novalidate v-on:submit.prevent>
      <div class="row m-t">
        <div class="offset-md-1 col-10" v-if="booking.createdAt" :title="createdAtTitle">
          <span class="float-right">
            <i class="fa fa-clock-o"></i>&nbsp;
            <small class="text-mutedCreated">{{ createdAgo }}</small>
            <small class="text-muted" id="created-by-text"
              >&nbsp;by <b>{{ booking.createdBy }}</b></small
            >
          </span>
        </div>

        <!-- Job selector -->
        <div
          class="offset-md-1 col-10 form-group"
          id="jobSelector"
          :class="{ 'has-error': jobSelectorInError }"
          v-if="booking.status !== bookingStatus.EXTERNAL"
        >
          <label class="col-12 col-form-label padding-left-removed" for="booking.jobNumber">Job</label>
          <div class="input-group">
            <TypeaheadComponent
              v-if="!isAdminType && !isNewJob"
              id="bookingJob"
              name="bookingJob"
              input-class="form-control m-r-xs"
              :value="jobQuery"
              :items="jobMatches"
              :get-label="jobLabel"
              :get-key="jobKey"
              :disabled="isReadOnly"
              v-on:input="onJobInput"
              v-on:select="onJobSelect"
            >
              <template v-slot:item="{ item, query }">
                <a tabindex="-1" href="#" v-html="highlight(label(item), query)"></a>
              </template>
            </TypeaheadComponent>
            <input v-if="isAdminType" class="form-control" id="bookingJob" type="text" name="bookingJob" disabled />
            <input
              v-if="isNewJob"
              class="form-control"
              id="bookingJob"
              type="text"
              name="bookingJob"
              disabled
              :value="job ? job.title : ''"
            />
            <span class="input-group-btn">
              <button
                class="btn btn-primary"
                id="newJobButton"
                type="button"
                :disabled="isAdminType"
                v-on:click="$emit('toggle-new-job')"
              >
                New Job
              </button>
            </span>
            <span class="input-group-btn">
              <button
                class="btn btn-primary"
                id="adminBooking"
                name="adminTypeSelector"
                type="button"
                :disabled="isNewJob || isReadOnly"
                style="border-radius: 3px"
                v-on:click="$emit('clear-booking-job')"
              >
                Admin
              </button>
            </span>
          </div>
          <small>
            <a
              class="form-text"
              id="jobLink"
              v-if="!isAdminType && !isNewJob && job && job.title && booking._id"
              v-on:click="$emit('open-job-window')"
              >Open the "{{ job.title }}" job</a
            >
          </small>
        </div>

        <!-- Linked job (external) -->
        <div
          class="offset-md-1 col-10 form-group"
          :class="{ 'has-error': jobSelectorInError }"
          v-if="booking.status === bookingStatus.EXTERNAL"
        >
          <label class="col-12 col-form-label padding-left-removed" for="booking.jobNumber">Linked Job</label>
          <div class="input-group">
            <TypeaheadComponent
              id="linkedJob"
              name="linkedJob"
              input-class="form-control m-r-xs"
              :value="linkedJobQuery"
              :items="jobMatches"
              :get-label="jobLabel"
              :get-key="jobKey"
              v-on:input="onLinkedJobInput"
              v-on:select="onLinkJobSelect"
            />
            <span class="input-group-btn">
              <button class="btn btn-primary" id="newJobButton" type="button" v-on:click="$emit('clear-booking-job')">
                New Job
              </button>
            </span>
          </div>
          <small>
            <a
              class="form-text"
              id="jobLink"
              v-if="!isAdminType && !isNewJob && job && job.title && booking._id"
              v-on:click="$emit('open-job-window')"
              >Open the "{{ job.title }}" job</a
            >
          </small>
        </div>
      </div>

      <!-- New job fields -->
      <div v-if="isNewJob && job">
        <div class="row">
          <div class="offset-md-1 col-10">
            <div class="form-group" :class="{ 'has-error': bookingJobTitleInvalid }">
              <label class="col-form-label" for="bookingJobTitle">Job Title</label>
              <input
                class="form-control"
                id="bookingJobTitle"
                v-model="job.title"
                :maxlength="maxTitleLength"
                type="text"
                name="bookingJobTitle"
                :placeholder="'Maximum ' + maxTitleLength + ' characters'"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="offset-md-1 col-5">
            <div class="form-group" :class="{ 'has-error': !selectedClient }">
              <label class="col-form-label" for="client">Client</label>
              <TypeaheadComponent
                id="bookingJobClient"
                name="client"
                :value="clientQuery"
                :items="clientMatches"
                :get-label="clientLabel"
                v-on:input="onClientInput"
                v-on:select="onClientSelect"
              />
            </div>
          </div>
          <div class="col-5">
            <div class="form-group" :class="{ 'has-error': !isAdminType && !job.contactPerson }">
              <label class="col-form-label" for="contactPerson">Contact Person</label>
              <select
                class="form-control"
                id="bookingJobContactPerson"
                v-model="job.contactPerson"
                name="contactPerson"
              >
                <option v-for="contact in sortedClientContacts" :key="contact" :value="contact">{{ contact }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="offset-md-1 col-5">
            <div class="form-group" :class="{ 'has-error': !isAdminType && !job.producer }">
              <label class="col-form-label" for="producer">Producer</label>
              <select class="form-control" id="bookingJobProducer" v-model="job.producer" name="producer">
                <option v-for="p in sortedProducers" :key="p._id || p.name" :value="p.name">{{ p.name }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Booking title -->
      <div class="row">
        <div class="offset-md-1 col-10">
          <label class="col-form-label" for="booking.bookingTitle">Booking Title</label>
          <input
            class="form-control"
            v-model="booking.bookingTitle"
            type="text"
            name="booking.bookingTitle"
            :disabled="isReadOnly"
          />
        </div>
      </div>

      <!-- Starts / Ends -->
      <div class="row m-t">
        <div
          class="offset-md-1 col-5 form-group"
          id="bookingStartsAt"
          :class="{ 'has-error': !dateValidate(booking.startsAt) }"
        >
          <label class="col-12 col-form-label padding-left-removed" for="booking.startsAt">Starts At</label>
          <DateTimeInput
            id="bookingStartsAtInput"
            :value="booking.startsAt"
            :disabled="isReadOnly"
            v-on:change="onStartsAtChange"
          />
        </div>
        <div
          class="col-5 form-group"
          id="bookingEndsAt"
          :class="{ 'has-error': !dateValidate(booking.endsAt) || endIsBeforeStart || doesntEndToday }"
        >
          <label class="col-12 col-form-label padding-left-removed" for="booking.endsAt">Ends At</label>
          <DateTimeInput
            id="bookingEndsAtInput"
            :value="booking.endsAt"
            :disabled="isReadOnly"
            v-on:change="onEndsAtChange"
          />
        </div>
      </div>

      <div class="row m-b" v-if="endIsBeforeStart">
        <div class="offset-md-1 col-10">
          <small class="text-danger" id="endIsBeforeStartError"
            >Make sure that the end of the booking is after the start</small
          >
        </div>
      </div>
      <div class="row m-b" v-if="doesntEndToday">
        <div class="offset-md-1 col-10">
          <small class="text-danger" id="doesntEndTodayError"
            >Make sure that the booking ends on the same day that it starts</small
          >
        </div>
      </div>
      <div class="row m-b" v-if="fifteenMinuteIntervals !== undefined">
        <div class="offset-md-1 col-10">
          <small class="text-warning" id="roundedTimesWarning">Booking times must be in 15 minute intervals</small>
        </div>
      </div>

      <!-- Repeat -->
      <div class="row">
        <div class="offset-md-1 col-5">
          <label class="col-12 col-form-label padding-left-removed" for="booking.repeatEvery">Repeat</label>
          <div class="input-group">
            <select
              class="form-control"
              v-model="booking.repeatEvery"
              name="booking.repeatEvery"
              :disabled="isReadOnly"
              v-on:change="updateRepeat"
            >
              <option :value="undefined"></option>
              <option v-for="date in dates" :key="date" :value="date">{{ date }}</option>
            </select>
            <span class="input-group-btn" v-if="booking.repeatEvery">
              <button class="btn btn-danger" type="button" data-testid="remove-repeat" v-on:click="removeRepeat">
                <i class="fa fa-close" id="removeRepeatButton"></i>
              </button>
            </span>
          </div>
        </div>
        <div class="col-5" v-if="booking.repeatEvery">
          <label class="col-12 col-form-label padding-left-removed" for="booking.endsOn">Repeat Ends On</label>
          <div class="input-group">
            <DateTimeInput
              data-testid="booking-repeat-endson"
              :value="booking.endsOn"
              :disabled="!booking.repeatEvery"
              v-on:change="onEndsOnChange"
            />
            <span class="input-group-btn">
              <button class="btn btn-primary" v-on:click="collapseRepeating = !collapseRepeating">
                <i class="fa fa-list" v-if="collapseRepeating"></i>
                <i class="fa fa-reply-all" v-else></i>
              </button>
            </span>
          </div>
        </div>
      </div>

      <div class="row m-t" v-if="booking.repeatEvery">
        <div class="offset-md-6 col-4">
          <div class="form-check">
            <button
              class="btn btn-default btn-xs"
              type="button"
              name="includeWeekends"
              :class="{ checked: booking.includeWeekends === true }"
              v-on:click="toggleIncludeWeekends"
            >
              <span
                class="glyphicon"
                style="width: 10px; left: -1px"
                :class="{ 'glyphicon-ok': booking.includeWeekends === true }"
              ></span>
            </button>
            <label for="includeWeekends">&nbsp; Include Weekends</label>
          </div>
        </div>
      </div>

      <div class="row m-t" v-if="!collapseRepeating">
        <div class="offset-md-1 col-10">
          <div class="card card-default">
            <div class="card-heading"><span>Bookings that will be created</span></div>
            <div class="card-body">
              <div class="row">
                <div class="col-12" v-for="(date, index) in repeatingDates" :key="index">
                  <p>{{ date.startsAt }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Status / Activity -->
      <div class="row" style="margin-top: 10px">
        <div class="offset-md-1 col-5 form-group" :class="{ 'has-error': !isAdminType && !booking.status }">
          <label class="col-12 col-form-label padding-left-removed" for="booking.status">Status</label>
          <!--
            The original rendered a different <select> per status: when the booking is quoting (or
            invoiced/external) only that option is offered, so its status cannot be changed back. A
            single select offering them all let a Quoting booking be set to Confirmed, which the API
            rejects with "You cannot move a job back to quoting status".
          -->
          <select
            v-if="
              booking.status !== bookingStatus.QUOTING && booking.status !== bookingStatus.UNFUCKABLE && !isReadOnly
            "
            class="form-control"
            v-model="booking.status"
            name="booking.status"
            :disabled="isAdminType"
          >
            <option :value="bookingStatus.HOLD">Hold</option>
            <option :value="bookingStatus.CONFIRMED">Confirmed</option>
            <option :value="bookingStatus.COMPLETED">Completed</option>
          </select>
          <select
            v-if="booking.status === bookingStatus.QUOTING && !isReadOnly"
            class="form-control"
            v-model="booking.status"
            name="booking.status"
            :disabled="isAdminType"
          >
            <option :value="bookingStatus.QUOTING">Quoting</option>
          </select>
          <select
            v-if="booking.status === bookingStatus.UNFUCKABLE"
            class="form-control"
            v-model="booking.status"
            name="booking.status"
            :disabled="isAdminType"
          >
            <option :value="bookingStatus.UNFUCKABLE">Invoiced</option>
          </select>
          <select
            v-if="booking.status === bookingStatus.EXTERNAL"
            class="form-control"
            v-model="booking.status"
            name="booking.status"
            :disabled="isAdminType"
          >
            <option :value="bookingStatus.EXTERNAL">External</option>
          </select>
        </div>
        <div class="col-5 form-group" id="activitySelector" :class="{ 'has-error': !booking.activity }">
          <label class="col-12 col-form-label padding-left-removed" for="booking.activity">Activity</label>
          <select class="form-control" v-model="booking.activity" name="booking.activity" :disabled="isReadOnly">
            <option :value="undefined"></option>
            <option v-for="activity in activities" :key="activity" :value="activity">{{ activity }}</option>
          </select>
        </div>
      </div>

      <div v-if="booking.status === bookingStatus.CONFIRMED">
        <div class="row">
          <div class="offset-md-1 col-4 m-b">
            <div class="form-check">
              <button
                class="btn btn-default btn-xs"
                type="button"
                name="criticalTask"
                :class="{ checked: booking.criticalTask === true }"
                v-on:click="toggleCriticalTask"
              >
                <span
                  class="glyphicon"
                  style="width: 10px; left: -1px"
                  :class="{ 'glyphicon-ok': booking.criticalTask === true }"
                ></span>
              </button>
              <label for="criticalTask">&nbsp; Critical Task</label>
            </div>
          </div>
        </div>
      </div>

      <!-- Resources -->
      <div class="row">
        <div
          class="offset-md-1 col-5 form-group"
          :class="{ 'has-error': !booking.bookingEvents || booking.bookingEvents.length === 0 }"
        >
          <label class="col-12 col-form-label padding-left-removed" for="booking.resources">Resources</label>
          <TypeaheadComponent
            name="booking.resources"
            placeholder="Type for resources..."
            :value="resourceQuery"
            :items="resourceMatches"
            :get-label="resourceLabel"
            :get-key="resourceKey"
            :enter-selects="false"
            :disabled="isReadOnly"
            v-on:input="onResourceInput"
            v-on:select="onResourceSelect"
          >
            <template v-slot:item="{ item }">
              <a tabindex="-1" href="#"><ResourceLabelComponent :resource="item" /></a>
            </template>
          </TypeaheadComponent>
        </div>
        <div class="col-5 m-b-none form-group">
          <label class="col-12 col-form-label padding-left-removed" for="booking.resources">Selected Resources</label>
          <div class="ibox">
            <ul class="todo-list small-list">
              <li
                v-for="bookingEvent in booking.bookingEvents"
                :key="bookingEvent.sequence"
                :id="'bookingResource-' + resourceName(bookingEvent.resource)"
                :class="{ 'background-warning': hasConflict(bookingEvent.resource._id) }"
              >
                <div>
                  <ResourceLabelComponent style="width: 135px" :resource="bookingEvent.resource" />
                  <button
                    class="btn btn-primary btn-xs float-right"
                    type="button"
                    :id="'deleteResourceButton-' + resourceName(bookingEvent.resource)"
                    v-if="!job || job.status !== jobStatus.INVOICED || isAdmin"
                    v-on:click="deleteResource(bookingEvent.resource)"
                  >
                    <i class="fa fa-close sandbox-m-r"></i>
                  </button>
                </div>
                <div v-if="hasConflict(bookingEvent.resource._id)">
                  <small class="text-warning">This resource has a conflicting booking!</small>
                </div>
              </li>
              <li
                v-for="attendee in booking.additionalAttendees"
                :key="attendee.emailAddress"
                :id="'bookingAttendee-' + attendee.emailAddress"
                style="background: #3798a9"
              >
                <div>
                  <AdditionalAttendeeLabelComponent :attendee="attendee" />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="row" v-if="!isAdminType && job">
        <div class="offset-md-1 col-10 form-group" :class="{ 'has-error': isNewJob && !job.description }">
          <label class="col-form-label" for="description">Description</label>
          <textarea
            class="form-control"
            id="bookingJobDescription"
            v-model="job.description"
            rows="4"
            name="description"
            style="width: 100%"
            :readonly="!isNewJob"
          ></textarea>
        </div>
      </div>

      <!-- Notes -->
      <div class="row">
        <div class="offset-md-1 col-10 form-group">
          <label class="col-form-label" for="booking.notes">Notes</label>
          <textarea
            class="form-control"
            v-model="booking.notes"
            rows="4"
            name="bookingNotes"
            style="width: 100%"
            :disabled="isReadOnly"
          ></textarea>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <div class="row" v-if="booking.status === bookingStatus.EXTERNAL">
          <div class="col-12">
            <p class="m-t-sm text-danger"><strong>Note: Outlook bookings can only be deleted from Outlook</strong></p>
          </div>
        </div>

        <div class="row m-b" v-if="openDeleteConfirm">
          <div class="col-12">
            <span><strong class="m-r">Deleting booking(s) cannot be undone, delete?</strong></span>
            <button
              class="btn btn-warning"
              type="button"
              id="cancelDeleteButton"
              title="Cancel Delete"
              v-on:click="openDeleteConfirm = false"
            >
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="button"
              id="confirmDeleteButton"
              title="Confirm Delete"
              v-on:click="confirmDelete"
            >
              Remove Booking(s)
            </button>
          </div>
        </div>

        <div class="row m-b" v-if="openUpdateAllConfirm">
          <div class="col-9">
            <span
              ><strong class="m-r"
                >Changing the structure of the repeat bookings will delete individual changes, update all?</strong
              ></span
            >
          </div>
          <div class="col-3">
            <button
              class="btn btn-warning"
              type="button"
              id="cancelDeleteUpdateButton"
              title="Cancel Delete"
              v-on:click="openUpdateAllConfirm = false"
            >
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="button"
              id="confirmUpdateAllButton"
              title="Confirm Update All"
              v-on:click="confirmUpdateAll"
            >
              Update All
            </button>
          </div>
        </div>

        <div class="row m-b" v-if="!openDeleteConfirm && !openUpdateAllConfirm">
          <div class="col-12">
            <button
              class="btn btn-warning"
              type="button"
              id="cancelBookingButton"
              title="Cancel (Esc)"
              v-on:click="$emit('cancel')"
            >
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="button"
              id="deleteBookingButton"
              :disabled="bookingIsNotInPast || isReadOnly"
              v-on:click="openDeleteConfirm = true"
            >
              Delete
            </button>
            <button
              class="btn btn-primary"
              type="button"
              id="okSaveBookingAddRepeatButton"
              v-if="booking.repeatEvery && !existingRepeat"
              :disabled="formIsInvalid"
              v-on:click="save('add-repeat')"
            >
              Save - Adding Repeats
            </button>
            <button
              class="btn btn-danger"
              type="button"
              id="okSaveBookingRemoveRepeatButton"
              v-if="!booking.repeatEvery && existingRepeat"
              :disabled="bookingIsNotInPast"
              v-on:click="removeRepeatAndSave"
            >
              Save - Deleting Repeats
            </button>
            <button
              class="btn btn-primary"
              type="button"
              id="okSaveBookingWithRepeatButton"
              v-if="booking.repeatEvery && existingRepeat"
              :disabled="bookingIsNotInPast"
              v-on:click="openUpdateAllConfirm = true"
            >
              Save - Updating Repeats
            </button>
            <button
              class="btn btn-primary"
              type="button"
              id="okSaveBookingNoRepeatButton"
              v-if="(booking.repeatEvery && existingRepeat) || (!booking.repeatEvery && !existingRepeat)"
              :disabled="formIsInvalid"
              v-on:click="save('save')"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import moment from 'moment-timezone'

import AdditionalAttendeeLabelComponent from '../../resources/component/additionalAttendeeLabelComponent.vue'
import DateTimeInput from '../../common/component/dateTimeInputComponent.vue'
import ResourceLabelComponent from '../../resources/component/resourceLabelComponent.vue'
import TypeaheadComponent from '../../common/component/typeaheadComponent.vue'
import { latestQuery } from '../../common/service/latestQuery.js'

export default {
  name: 'BookingComponent',
  components: { AdditionalAttendeeLabelComponent, DateTimeInput, ResourceLabelComponent, TypeaheadComponent },
  props: {
    // The prepared booking and current job, owned by BookingCtrl. The component reads and
    // mutates them directly: ngVue passes the same object references, which Vue observes, so
    // both sides see the other's edits without a clone-and-emit per field.
    booking: {
      type: Object,
      required: true,
    },
    job: {
      type: Object,
      default: null,
    },
    existingBooking: {
      type: Object,
      default: () => ({}),
    },
    isNewJob: {
      type: Boolean,
      default: false,
    },
    isAdminType: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    isNewBooking: {
      type: Boolean,
      default: false,
    },
    hasRepeating: {
      type: Boolean,
      default: false,
    },
    selectedClient: {
      type: Object,
      default: null,
    },
    clientContacts: {
      type: Array,
      default: () => [],
    },
    producers: {
      type: Array,
      default: () => [],
    },
    allResources: {
      type: Array,
      default: () => [],
    },
    repeatingDates: {
      type: Array,
      default: () => [],
    },
    // Returns BookingService.getBookingsForResources() - passed as a function so the component
    // always reads the current cache (the service replaces the object on every refresh).
    getBookingsForResources: {
      type: Function,
      required: true,
    },
    existingResourceIds: {
      type: Array,
      default: () => [],
    },
    activities: {
      type: Array,
      default: () => [],
    },
    dates: {
      type: Array,
      default: () => [],
    },
    maxTitleLength: {
      type: Number,
      default: 50,
    },
    linkedJobTitle: {
      type: String,
      default: '',
    },
    bookingStatus: {
      type: Object,
      default: () => ({}),
    },
    jobStatus: {
      type: Object,
      default: () => ({}),
    },
    // Function props - the lookup/formatting helpers stay on the Angular side for now.
    findJob: {
      type: Function,
      required: true,
    },
    findClients: {
      type: Function,
      required: true,
    },
    label: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // seeded from the job the booking already has, so reopening shows its "number - title"
      jobQuery: this.job && this.label ? this.label(this.job) || '' : '',
      linkedJobQuery: this.linkedJobTitle,
      clientQuery: '',
      resourceQuery: '',
      jobMatches: [],
      clientMatches: [],
      resourceMatches: [],
      collapseRepeating: true,
      openDeleteConfirm: false,
      openUpdateAllConfirm: false,
      delType: undefined,
      fifteenMinuteIntervals: undefined,
    }
  },
  computed: {
    existingRepeat() {
      return this.existingBooking.repeatEvery
    },
    hours() {
      const starts = moment(this.booking.startsAt, this.displayDateFormat)
      const ends = moment(this.booking.endsAt, this.displayDateFormat)
      const duration = moment.duration(ends.diff(starts))
      this.booking.hours = duration.asHours()
      return this.booking.hours
    },
    createdAtTitle() {
      return this.booking.createdAt ? moment(this.booking.createdAt).format('MMM d, y h:mm a') : ''
    },
    createdAgo() {
      return this.booking.createdAt ? moment(this.booking.createdAt).fromNow() : ''
    },
    displayDateFormat() {
      return 'DD-MM-YYYY HH:mm'
    },
    jobSelectorInError() {
      return !this.jobIsSet && !this.isAdminType && !this.isNewJob
    },
    jobIsSet() {
      return this.job !== null || this.isAdminType
    },
    bookingJobTitleInvalid() {
      return !this.isAdminType && (!this.job || !this.job.title || this.job.title.length > this.maxTitleLength)
    },
    sortedClientContacts() {
      return [...this.clientContacts].sort()
    },
    sortedProducers() {
      return [...this.producers].sort((a, b) => (a.name > b.name ? 1 : -1))
    },
    endIsBeforeStart() {
      const starts = moment(this.booking.startsAt, this.displayDateFormat)
      const ends = moment(this.booking.endsAt, this.displayDateFormat)
      return ends.isBefore(starts)
    },
    doesntEndToday() {
      const starts = moment(this.booking.startsAt, this.displayDateFormat)
      const ends = moment(this.booking.endsAt, this.displayDateFormat).subtract(1, 'minute')
      return starts.isValid() && ends.isValid() && !ends.isSame(starts, 'day')
    },
    formIsInvalid() {
      return (
        !this.jobIsSet ||
        !this.booking.activity ||
        !this.booking.bookingEvents ||
        this.booking.bookingEvents.length === 0 ||
        !this.dateValidate(this.booking.startsAt) ||
        !this.dateValidate(this.booking.endsAt) ||
        this.bookingNotEditable ||
        this.endIsBeforeStart ||
        (!this.isAdminType && this.job !== null && this.job.description === undefined) ||
        this.doesntEndToday ||
        this.bookingJobTitleInvalid
      )
    },
    bookingNotEditable() {
      if (this.isAdminType) {
        return false
      }
      return Boolean(this.job && this.job.status === this.jobStatus.INVOICED && !this.isAdmin)
    },
    bookingIsNotInPast() {
      return (
        !this.isAdmin &&
        (this.formIsInvalid || moment(this.booking.startsAt, this.displayDateFormat).isBefore(moment(), 'day'))
      )
    },
  },
  created() {
    // Only the newest query may fill each list (#683). The job search indexes every prefix of every
    // word, so "c" matches a page of unrelated jobs - and when that answer arrived after the "crystal"
    // one it replaced the job the user was about to click, which made linking a booking to an
    // existing job number almost impossible.
    // 300ms is what `typeahead-wait-ms` used before the Vue rewrite lost it, so a seven-character
    // search is one request rather than seven. The input itself is not delayed - only the lookup.
    this.jobLookup = latestQuery(300)
    this.clientLookup = latestQuery(300)
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  watch: {
    job(value) {
      if (!this.isNewJob) {
        this.jobQuery = value && this.label ? this.label(value) || '' : ''
      }
    },
    linkedJobTitle(value) {
      this.linkedJobQuery = value
    },
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    dateValidate(date) {
      return moment(date, this.displayDateFormat, true).isValid()
    },
    resourceName(resource) {
      return resource ? resource.name : ''
    },
    resourceLabel(resource) {
      return resource ? resource.name : ''
    },
    resourceKey(resource) {
      return resource ? resource._id : undefined
    },
    jobLabel(item) {
      return this.label(item)
    },
    jobKey(item) {
      return item && item.jobNumber
    },
    clientLabel(client) {
      return client.accountName
    },
    highlight(text, query) {
      if (!text || !query) {
        return text
      }
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return text.replace(new RegExp(escaped, 'ig'), match => `<strong>${match}</strong>`)
    },
    // --- typeaheads ---
    onJobInput(value) {
      this.jobQuery = value
      if (!value) {
        this.jobLookup.cancel()
        this.jobMatches = []
        return
      }
      this.jobLookup(
        () => this.findJob(value),
        matches => {
          this.jobMatches = matches || []
        }
      )
    },
    onLinkedJobInput(value) {
      this.linkedJobQuery = value
      this.onJobInput(value)
    },
    onJobSelect(job) {
      this.jobLookup.cancel()
      this.jobQuery = this.label(job)
      this.jobMatches = []
      this.$emit('select-job', job)
    },
    onLinkJobSelect(job) {
      this.jobLookup.cancel()
      this.linkedJobQuery = this.label(job)
      this.jobMatches = []
      this.$emit('link-job', job)
    },
    onClientInput(value) {
      this.clientQuery = value
      if (this.job) {
        this.job.client = value
      }
      if (!value) {
        this.clientLookup.cancel()
        this.clientMatches = []
        return
      }
      this.clientLookup(
        () => this.findClients(value),
        matches => {
          this.clientMatches = matches || []
        }
      )
    },
    onClientSelect(client) {
      this.clientLookup.cancel()
      this.clientQuery = client.accountName
      this.clientMatches = []
      this.$emit('select-client', client)
    },
    onResourceInput(value) {
      this.resourceQuery = value
      this.resourceMatches = this.findResource(value)
    },
    findResource(query) {
      const selected = (this.booking.bookingEvents || []).map(event => event.resource && event.resource._id)
      const escaped = (query || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const pattern = new RegExp(escaped, 'i')
      return this.allResources.filter(resource => pattern.test(resource.name) && !selected.includes(resource._id))
    },
    onResourceSelect(resource) {
      this.resourceQuery = ''
      this.resourceMatches = []
      this.$emit('select-resource', resource)
    },
    deleteResource(resource) {
      this.booking.bookingEvents = this.booking.bookingEvents.filter(event => event.resource._id !== resource._id)
    },
    // --- conflicts ---
    hasConflict(resourceId) {
      if (this.existingResourceIds.includes(resourceId)) {
        return false
      }
      const bookings = this.getBookingsForResources()[resourceId] || []
      const starts = moment(this.booking.startsAt, this.displayDateFormat)
      const ends = moment(this.booking.endsAt, this.displayDateFormat)
      return bookings.some(existing => {
        const existingStart = moment(existing.startsAt)
        const existingEnd = moment(existing.endsAt)
        return (
          (starts.isSame(existingStart) && ends.isSame(existingEnd)) ||
          starts.isBetween(existingStart, existingStart) ||
          ends.isBetween(existingStart, existingEnd) ||
          existingStart.isBetween(starts, ends) ||
          existingEnd.isBetween(starts, ends)
        )
      })
    },
    // --- times ---
    roundDateDown(date) {
      const dateTime = moment(date, this.displayDateFormat).toDate()
      dateTime.setMinutes(Math.floor(dateTime.getMinutes() / 15) * 15)
      return dateTime
    },
    roundDateUp(date) {
      const dateTime = moment(date, this.displayDateFormat).toDate()
      dateTime.setMinutes(Math.ceil(dateTime.getMinutes() / 15) * 15)
      return dateTime
    },
    roundTimes() {
      const startDate = moment(this.booking.startsAt, this.displayDateFormat).toDate()
      const endDate = moment(this.booking.endsAt, this.displayDateFormat).toDate()
      const roundedStartDate = this.roundDateDown(startDate)
      const roundedEndDate = this.roundDateUp(endDate)
      if (
        (startDate.getMinutes() !== roundedStartDate.getMinutes() && !isNaN(roundedStartDate)) ||
        (endDate.getMinutes() !== roundedEndDate.getMinutes() && !isNaN(roundedEndDate))
      ) {
        this.fifteenMinuteIntervals = true
      } else {
        this.fifteenMinuteIntervals = undefined
      }
      this.booking.startsAt = moment(roundedStartDate).format(this.displayDateFormat)
      this.booking.endsAt = moment(roundedEndDate).format(this.displayDateFormat)
    },
    onStartsAtChange(value) {
      this.booking.startsAt = value
      this.shiftEndsAt(value)
      this.roundTimes()
      this.updateRepeat()
    },
    onEndsAtChange(value) {
      this.booking.endsAt = value
      this.roundTimes()
    },
    onEndsOnChange(value) {
      this.booking.endsOn = value
      this.updateRepeat()
    },
    shiftEndsAt(startsAt) {
      this.booking.endsAt = moment(startsAt, this.displayDateFormat)
        .add(this.existingBooking.hours, 'hours')
        .format(this.displayDateFormat)
    },
    // --- repeat ---
    updateRepeat() {
      if (!this.booking.repeatEvery) {
        this.booking.repeating = null
        this.$emit('update-repeat', this.booking)
        return
      }
      this.booking.repeating = { displayDateFormat: this.displayDateFormat }
      this.$emit('update-repeat', this.booking)
    },
    removeRepeat() {
      this.booking.repeatEvery = undefined
      this.updateRepeat()
    },
    toggleIncludeWeekends() {
      // A plain assignment: the key may not exist yet, and Vue 3's proxy reactivity sees a new key
      // without `$set` (which Vue 3 removed).
      this.booking.includeWeekends = !this.booking.includeWeekends
      this.updateRepeat()
    },
    toggleCriticalTask() {
      this.booking.criticalTask = !this.booking.criticalTask
    },
    // --- save / delete ---
    save(mode) {
      if (this.formIsInvalid) {
        return
      }
      if (this.hasRepeating && mode === 'add-repeat') {
        this.openDeleteConfirm = true
        this.delType = 'repeat'
        return
      }
      this.$emit('save', { mode, booking: this.booking, job: this.job })
    },
    removeRepeatAndSave() {
      this.delType = 'repeat'
      this.openDeleteConfirm = true
    },
    confirmUpdateAll() {
      this.openUpdateAllConfirm = false
      this.$emit('save', { mode: 'update-repeat', booking: this.booking, job: this.job })
    },
    confirmDelete() {
      this.$emit('delete', { type: this.delType, booking: this.booking })
    },
    // --- hotkeys ---
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('cancel')
      } else if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault()
        this.save('save')
      }
    },
  },
}
</script>
